import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography,
  CircularProgress, IconButton, Radio, Checkbox, Link, Collapse, Chip, Tooltip, Divider,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, ExpandMore, ChevronRight, Star, StarBorder } from '@mui/icons-material';
import { logger } from '../utils/logger';
import { gettyApi, TreeNode } from '../utils/gettyApi';
import { buildTreeSearchIndex, searchTreeIndex } from '../utils/treeSearchIndex';
import { ObjectMetadataValue, HIERARCHY_ROOT_IDS } from '../types/ObjectHierarchicalMetadataValue';
import { useGettyFavorites } from '../hooks/useGettyFavorites';
import { VocabularyType } from '../utils/gettyFavoritesApi';

// Extend TreeNode with optional isGuideterm flag for display purposes
interface DisplayTreeNode extends TreeNode {
  isGuideterm?: boolean;
}

interface ObjectHierarchicalPickerProps {
  hierarchyRootId: string;
  label: string;
  value: ObjectMetadataValue | ObjectMetadataValue[];
  onChange: (value: ObjectMetadataValue | ObjectMetadataValue[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  /**
   * When set, each selected chip in the input renders this short tag as a
   * prefix (e.g. "AAT") to surface the vocabulary source. Opt-in only;
   * legacy call sites omit it and keep the chip-without-prefix behavior.
   */
  chipPrefix?: string;
  /**
   * Visual size of the read-only trigger field. `'large'` raises minHeight
   * and font size for higher-density pickers (Techniques, Materials,
   * Substrate). Defaults to `'medium'`.
   */
  triggerSize?: 'medium' | 'large';
}

// Map label prop to VocabularyType for favorites
const labelToVocabularyType = (label: string): VocabularyType | null => {
  const normalizedLabel = label.toLowerCase();
  if (normalizedLabel.includes('work type')) return 'work_type';
  if (normalizedLabel.includes('technique')) return 'techniques';
  if (normalizedLabel.includes('material')) return 'materials';
  if (normalizedLabel.includes('substrate')) return 'substrate';
  return null;
};

export const ObjectHierarchicalPicker = ({
  hierarchyRootId, label, value, onChange, multiple = false, disabled, chipPrefix, triggerSize = 'medium'
}: ObjectHierarchicalPickerProps) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [treeData, setTreeData] = useState<DisplayTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(''); // stores node.id
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set()); // stores node.id values
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [initialExpandedNodes] = useState<Set<string>>(new Set()); // Store initial state
  const [freetextMode, setFreetextMode] = useState(false);
  const [freetextValue, setFreetextValue] = useState('');

  // Getty favorites functionality
  const { isFavorite, toggleFavorite, getFavoritesForType } = useGettyFavorites();
  const vocabularyType = useMemo(() => labelToVocabularyType(label), [label]);
  const favorites = useMemo(
    () => vocabularyType ? getFavoritesForType(vocabularyType) : [],
    [vocabularyType, getFavoritesForType]
  );

  const valueArray = Array.isArray(value) ? value : (value ? [value] : []);

  /**
   * Load pre-built hierarchy tree from backend.
   * The backend now returns a ready-to-use tree structure,
   * eliminating the need for client-side tree building.
   */
  const loadHierarchy = useCallback(async () => {
    setLoading(true);
    setTreeData([]);
    try {
      let response;

      // Map hierarchy root IDs to Getty API methods
      switch (hierarchyRootId) {
        case HIERARCHY_ROOT_IDS.workTypes:
          response = await gettyApi.getWorkTypesHierarchy();
          break;
        case HIERARCHY_ROOT_IDS.materials:
        case HIERARCHY_ROOT_IDS.substrates:
          response = await gettyApi.getMaterialsHierarchy();
          break;
        case HIERARCHY_ROOT_IDS.techniques:
          response = await gettyApi.getTechniquesHierarchy();
          break;
        default:
          throw new Error(`Unknown hierarchy root ID: ${hierarchyRootId}`);
      }

      // Use pre-built tree directly from backend response
      if (response?.tree) {
        // Add isGuideterm flag for nodes with labels like "<guide term>"
        const addGuidetermFlags = (nodes: TreeNode[]): DisplayTreeNode[] => {
          return nodes.map(node => ({
            ...node,
            isGuideterm: node.label.startsWith('<') && node.label.endsWith('>'),
            children: node.children ? addGuidetermFlags(node.children) : []
          }));
        };

        const treeWithFlags = addGuidetermFlags(response.tree);
        setTreeData(treeWithFlags);
      }
    } catch (error) {
      logger.error(`Failed to load hierarchy for ${hierarchyRootId}`, error);
    } finally {
      setLoading(false);
    }
  }, [hierarchyRootId]);

  // Load hierarchy when dialog opens
  useEffect(() => {
    if (open) {
      loadHierarchy();
      // Reset freetext mode and selections when opening dialog
      setFreetextMode(false);
      setFreetextValue('');
      setSelectedValue('');
      setSelectedValues(new Set());
    }
  }, [open, loadHierarchy]);

  // Debounce filter value to reduce expensive tree traversals
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterValue(filterValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValue]);

  const findNodeByLabel = (nodes: DisplayTreeNode[], targetLabel: string): DisplayTreeNode | null => {
    for (const node of nodes) {
      if (node.label === targetLabel) return node;
      const found = findNodeByLabel(node.children || [], targetLabel);
      if (found) return found;
    }
    return null;
  };

  // Alternative breadcrumb search that builds path while searching
  const findNodePathAlternative = (nodes: DisplayTreeNode[], targetId: string, targetLabel: string): string[] => {
    const searchWithPath = (nodes: DisplayTreeNode[], path: string[]): string[] => {
      for (const node of nodes) {
        const currentPath = [...path, node.label];

        // Try matching by both ID and label
        if (node.id === targetId || node.label === targetLabel) {
          return currentPath;
        }

        // Search children
        const childResult = searchWithPath(node.children || [], currentPath);
        if (childResult.length > 0) {
          return childResult;
        }
      }
      return [];
    };

    return searchWithPath(nodes, []);
  };

  const getNodePath = (nodes: DisplayTreeNode[], targetId: string): string[] => {
    const findPath = (nodes: DisplayTreeNode[], path: string[] = []): string[] | null => {
      for (const node of nodes) {
        const currentPath = [...path, node.label];
        if (node.id === targetId) {
          return currentPath;
        }
        const found = findPath(node.children || [], currentPath);
        if (found) return found;
      }
      return null;
    };

    return findPath(nodes) || [];
  };

  const findNodeById = (nodes: DisplayTreeNode[], targetId: string): DisplayTreeNode | null => {
    for (const node of nodes) {
      if (node.id === targetId) return node;
      const found = findNodeById(node.children || [], targetId);
      if (found) return found;
    }
    return null;
  };

  const buildNodeValue = (nodeId: string): ObjectMetadataValue => {
    const node = findNodeById(treeData, nodeId);
    let breadcrumbs: string[] = [];

    if (node) {
      breadcrumbs = getNodePath(treeData, node.id);
      if (breadcrumbs.length === 0) {
        breadcrumbs = findNodePathAlternative(treeData, node.id, node.label);
      }
    }

    const label = node?.label ?? nodeId;

    if (breadcrumbs.length === 0) {
      breadcrumbs = [label];
    }

    return {
      textValue: label,
      isFreetext: false,
      valueUri: `http://vocab.getty.edu/page/aat/${nodeId}`,
      breadcrumbs
    };
  };

  const handleSelect = () => {
    if (freetextMode) {
      const newValue: ObjectMetadataValue = { textValue: freetextValue, isFreetext: true };
      if (multiple) {
        onChange([...valueArray, newValue]);
      } else {
        onChange(newValue);
      }
    } else if (multiple) {
      const newValues = Array.from(selectedValues).map(buildNodeValue);
      onChange([...valueArray, ...newValues]);
    } else {
      onChange(buildNodeValue(selectedValue));
    }

    setOpen(false);
    setSelectedValue('');
    setSelectedValues(new Set());
    setFreetextMode(false);
    setFreetextValue('');
  };

  const handleRemove = (index: number) => {
    if (multiple) {
      const newArray = valueArray.filter((_, i) => i !== index);
      onChange(newArray);
    }
  };



  // Pre-computed flat search index — built once when tree data loads (~5ms for 5000+ nodes)
  const searchIndex = useMemo(() => buildTreeSearchIndex(treeData), [treeData]);

  // Index-based search: scans flat array with pre-lowercased labels, returns visible IDs
  const { visibleNodeIds, searchExpandedNodes } = useMemo(() => {
    const result = searchTreeIndex(searchIndex, debouncedFilterValue);
    if (!result) {
      return { visibleNodeIds: null, searchExpandedNodes: null };
    }

    // During search, expand all visible nodes that have children in the visible set
    const expanded = new Set<string>();
    for (const node of searchIndex.nodes) {
      if (result.has(node.id) && node.descendantIds.some((d) => result.has(d))) {
        expanded.add(node.id);
      }
    }

    return { visibleNodeIds: result, searchExpandedNodes: expanded };
  }, [searchIndex, debouncedFilterValue]);

  // Sync expansion state from memoized search results
  useEffect(() => {
    if (searchExpandedNodes) {
      setExpandedNodes(searchExpandedNodes);
    } else {
      setExpandedNodes(new Set(initialExpandedNodes));
    }
  }, [searchExpandedNodes, initialExpandedNodes]);

  // Pre-compute highlight regex once per debounced search term (#788)
  const highlightRegex = useMemo(() => {
    const term = debouncedFilterValue.trim();
    if (!term) return null;
    return new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  }, [debouncedFilterValue]);

  const highlightText = (text: string) => {
    if (!highlightRegex) return text;

    const parts = text.split(highlightRegex);

    return (
      <>
        {parts.map((part, index) =>
          highlightRegex.test(part) ? (
            <Box key={index} component="span" sx={{ backgroundColor: 'yellow', fontWeight: 'bold' }}>
              {part}
            </Box>
          ) : (
            part
          )
        )}
      </>
    );
  };

  const renderTreeItems = (nodes: DisplayTreeNode[], level = 0) => {
    const visibleNodes = visibleNodeIds
      ? nodes.filter((node) => visibleNodeIds.has(node.id))
      : nodes;
    return visibleNodes.map(node => (
      <Box key={node.id} sx={{ ml: level * 1.5 }}>
        <Box data-testid="tree-row" sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
          <Box data-testid="expand-button-slot" sx={{ width: 28, minWidth: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {(node.children?.length ?? 0) > 0 && (
              <IconButton size="small" onClick={() => {
                const newExpanded = new Set(expandedNodes);
                if (newExpanded.has(node.id)) {
                  newExpanded.delete(node.id);
                } else {
                  newExpanded.add(node.id);
                }
                setExpandedNodes(newExpanded);
              }} sx={{ p: 0 }}>
                {expandedNodes.has(node.id) ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            )}
          </Box>
          {!node.isGuideterm && multiple && (
            <Checkbox
              checked={selectedValues.has(node.id)}
              onChange={() => {
                const next = new Set(selectedValues);
                if (next.has(node.id)) {
                  next.delete(node.id);
                } else {
                  next.add(node.id);
                }
                setSelectedValues(next);
              }}
              size="small"
              sx={{ p: 0, mr: 0.5, width: 24, height: 24 }}
            />
          )}
          {!node.isGuideterm && !multiple && (
            <Radio
              checked={selectedValue === node.id}
              onChange={() => setSelectedValue(node.id)}
              size="small"
              sx={{ p: 0, mr: 0.5, width: 24, height: 24 }}
            />
          )}
          {!node.isGuideterm && vocabularyType && (
            <Tooltip title={isFavorite(vocabularyType, `http://vocab.getty.edu/page/aat/${node.id}`) ? "Remove from favorites" : "Add to favorites"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const uri = `http://vocab.getty.edu/page/aat/${node.id}`;
                  const breadcrumbs = getNodePath(treeData, node.id);
                  toggleFavorite(vocabularyType, uri, node.label, breadcrumbs);
                }}
                sx={{ p: 0, mr: 0.5 }}
              >
                {isFavorite(vocabularyType, `http://vocab.getty.edu/page/aat/${node.id}`) ? (
                  <Star fontSize="small" sx={{ color: 'gold' }} />
                ) : (
                  <StarBorder fontSize="small" sx={{ color: 'action.disabled' }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="body2"
            sx={{
              fontStyle: node.isGuideterm ? 'italic' : 'normal',
              color: node.isGuideterm ? 'text.secondary' : 'text.primary',
              cursor: node.isGuideterm ? 'default' : 'pointer'
            }}
            onClick={() => {
              if (node.isGuideterm) return;
              if (multiple) {
                const next = new Set(selectedValues);
                if (next.has(node.id)) {
                  next.delete(node.id);
                } else {
                  next.add(node.id);
                }
                setSelectedValues(next);
              } else {
                setSelectedValue(node.id);
              }
            }}
          >
            {node.isGuideterm ? (
              `<${node.label.replace(/^<|>$/g, '')}>`
            ) : (
              highlightText(node.label)
            )}
          </Typography>
        </Box>
        {(node.children?.length ?? 0) > 0 && (
          <Collapse in={expandedNodes.has(node.id)}>
            {renderTreeItems(node.children || [], level + 1)}
          </Collapse>
        )}
      </Box>
    ));
  };

  const displayValue = () => {
    if (multiple) {
      return ''; // Empty for multiple selection - chips show the values
    }
    return valueArray[0]?.textValue || '';
  };

  const renderChips = () => {
    if (!multiple || valueArray.length === 0) return null;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mr: 1 }}>
        {valueArray.map((item, index) => {
          const showPrefix = Boolean(chipPrefix) && !item.isFreetext;
          const chipLabel = showPrefix ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  bgcolor: 'action.hover',
                  px: 0.5,
                  py: 0.125,
                  borderRadius: 0.5,
                }}
              >
                {chipPrefix}
              </Box>
              <Box component="span">{item.textValue}</Box>
            </Box>
          ) : (
            item.textValue
          );
          return (
            <Chip
              key={index}
              label={chipLabel}
              onDelete={(e) => { e.stopPropagation(); handleRemove(index); }}
              size="small"
              variant={item.isFreetext ? "outlined" : "filled"}
              sx={{
                fontStyle: item.isFreetext ? 'italic' : 'normal',
                borderStyle: item.isFreetext ? 'dashed' : 'solid',
                fontSize: 14,
                height: 28,
              }}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <TextField
        label={label}
        value={displayValue()}
        onClick={() => setOpen(true)}
        InputProps={{
          readOnly: true,
          startAdornment: renderChips(),
          sx: {
            ...(multiple && valueArray.length > 0 ? { flexWrap: 'wrap', py: 0.5 } : {}),
            ...(triggerSize === 'large' ? { minHeight: 56, fontSize: 16 } : {}),
          },
        }}
        disabled={disabled}
        fullWidth
        placeholder={multiple && valueArray.length > 0 ? '' : `Click to select ${label.toLowerCase()}`}
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenMobile}
        PaperProps={{ sx: { height: fullScreenMobile ? '100%' : '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Select {label}
          <IconButton onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        {/* Flex column with overflow:hidden so only the tree region scrolls.
            Anchors the search box at the top and the Selected/Favorites/freetext
            controls at the bottom; otherwise favorites get pushed below the
            viewport on tall vocabularies. */}
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', pb: 0 }}>
          {!freetextMode ? (
            <>
              <TextField
                placeholder={`Search ${label.toLowerCase()}...`}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                fullWidth
                sx={{ mb: 2, flexShrink: 0 }}
                autoFocus
              />

              <Box
                data-testid="picker-tree-scroll"
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  p: 1,
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  renderTreeItems(treeData)
                )}
              </Box>

              {/* Selected value display */}
              {(selectedValue || selectedValues.size > 0) && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, flexShrink: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Selected:
                  </Typography>
                  {multiple ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {Array.from(selectedValues).map((nodeId) => {
                        const node = findNodeById(treeData, nodeId);
                        const displayLabel = node?.label ?? nodeId;
                        return <Chip key={nodeId} label={displayLabel} size="small" />;
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {findNodeById(treeData, selectedValue)?.label ?? selectedValue}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  Not in list?{' '}
                  <Link
                    component="button"
                    onClick={() => setFreetextMode(true)}
                    sx={{ textDecoration: 'underline' }}
                  >
                    enter freetext
                  </Link>
                </Typography>
              </Box>

              {favorites.length > 0 && (
                <>
                  <Divider role="presentation" sx={{ my: 2, flexShrink: 0 }} />
                  <Box sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', borderRadius: 1, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Star fontSize="small" sx={{ color: 'gold', mr: 0.5 }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        Favorites
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                      {favorites.map((fav) => {
                        const favNodeId = fav.uri.split('/').pop() ?? '';
                        const isFavSelected = multiple ? selectedValues.has(favNodeId) : selectedValue === favNodeId;
                        return (
                          <Chip
                            key={fav.uri}
                            label={fav.textValue}
                            size="small"
                            onClick={() => {
                              if (multiple) {
                                const next = new Set(selectedValues);
                                if (next.has(favNodeId)) {
                                  next.delete(favNodeId);
                                } else {
                                  next.add(favNodeId);
                                }
                                setSelectedValues(next);
                              } else {
                                setSelectedValue(favNodeId);
                              }
                            }}
                            onDelete={() => vocabularyType && toggleFavorite(vocabularyType, fav.uri, fav.textValue, fav.breadcrumbs)}
                            deleteIcon={<Star fontSize="small" sx={{ color: 'gold' }} />}
                            variant={isFavSelected ? "filled" : "outlined"}
                            color={isFavSelected ? "primary" : "default"}
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </>
              )}
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>Enter Custom {label}</Typography>
              <TextField
                label="Text"
                value={freetextValue}
                onChange={(e) => setFreetextValue(e.target.value)}
                fullWidth
                autoFocus
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSelect}
            variant="contained"
            disabled={freetextMode ? !freetextValue.trim() : (multiple ? selectedValues.size === 0 : !selectedValue)}
          >
            {freetextMode ? 'Save' : (multiple ? 'Add' : 'Select')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
